import React, { FC, useState } from 'react';
import { Chip, Grid, TableCell, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { find } from 'lodash';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { useTranslation } from 'react-i18next';

import { SharesItemFragment, useDeleteScheduledSharesMutation } from 'api';
import { Alert, CustomSVG, ICustomSVG } from 'components';
import { isUrl } from 'features/content/helpers';
import { getSocialAccountName } from 'helpers';
import { useNotify, useSocialConnections } from 'hooks';
import { useDeleteSharesItems } from 'features/activity/updaters';

import UrlMetadata from '../UrlMetadata';
import Body from '../Body';

interface IProps {
  item: SharesItemFragment;
}

const useStyles = makeStyles(theme => ({
  cell: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  content: {
    paddingLeft: theme.spacing(2),
  },
  chip: {
    backgroundColor: theme.palette.grey[200],
  },
  twitter: {
    color: theme.custom.networks.twitter,
  },
  linkedin: {
    color: theme.custom.networks.linkedin,
  },
}));

const ScheduledShareCell: FC<IProps> = ({ item }) => {
  const classes = useStyles();
  const notify = useNotify();
  const { t } = useTranslation();
  const { connections } = useSocialConnections();

  const [open, setOpen] = useState(false);
  const { content } = item;

  const deleteSharesItemsOptions = useDeleteSharesItems({
    scheduled: true,
    group_by_session: true,
    data: {
      limit: 10,
    },
  });

  const { mutate } = useDeleteScheduledSharesMutation({
    onSuccess: handleNotification,
    ...deleteSharesItemsOptions,
  });

  function handleNotification() {
    notify.info({ message: t('posting:deleteScheduledShareSuccess') });
  }

  function handleDelete(item: SharesItemFragment) {
    mutate({ ids: [item.id] });

    setOpen(false);
  }

  function getSocialAccount(item: SharesItemFragment) {
    const { network_id } = item;
    const account = find(connections, ['id', network_id]);

    return getSocialAccountName(account);
  }

  function renderNetwork(item: SharesItemFragment) {
    return (
      <Grid item key={item.id}>
        <Grid container direction="column" wrap="nowrap" spacing={1}>
          <Grid item xs>
            <Chip
              classes={{ root: classes.chip, icon: classes[item.network] }}
              icon={<CustomSVG name={item.network as keyof ICustomSVG} />}
              label={getSocialAccount(item)}
              deleteIcon={<CloseRoundedIcon />}
              onDelete={() => setOpen(true)}
            />
          </Grid>
          <Grid item xs>
            <Typography variant="body1">{item.user_commentary}</Typography>
          </Grid>
        </Grid>

        <Alert
          open={open}
          onConfirm={() => handleDelete(item)}
          onCancel={() => setOpen(false)}
          message={t('posting:deleteScheduledShareMessage')}
          confirm={t('common:delete')}
        />
      </Grid>
    );
  }

  if (!content) {
    return <TableCell align="left" classes={{ body: classes.cell }} />;
  }

  return (
    <TableCell align="left" classes={{ body: classes.cell }}>
      <Grid container direction="column" spacing={2} className={classes.content}>
        {renderNetwork(item)}
        <Grid item xs>
          {isUrl(content) ? <UrlMetadata content={content} /> : <Body content={content} />}
        </Grid>
      </Grid>
    </TableCell>
  );
};

export default ScheduledShareCell;
